<script lang="ts">
  /**
   * Cross-portal search, in the header on every page.
   *
   * "The single feature that makes the front door worth opening rather than
   * bookmarking each portal." It was buried in the old landing page's markup; living
   * in Shell's header slot means it is reachable from Status and Estate too, which is
   * where you are standing when you realise you were looking for a document.
   *
   * Fabric does the fan-out server-side (item 21): it queries every registered
   * module's `searchUrl` with the caller's claims applied, in parallel, and reports
   * which ones did not answer in time. **That reporting is the important part.** A
   * module silently omitted reads as "nothing found", and somebody would conclude the
   * document is not in Vault and go looking for the paper copy.
   *
   * Results render in a popover rather than replacing the page, so a search that
   * finds nothing does not cost you the screen you were on.
   */
  import { Icon } from "@steyncd/steyn-design";
  import { api, ApiError } from "../lib/api";
  import { estate } from "../lib/estate.svelte";

  type Hit = {
    portalId: string;
    title: string;
    subtitle: string | null;
    url: string;
    kind: string | null;
    at: string | null;
  };
  type Group = { portalId: string; name: string; hits: Hit[] };

  let q = $state("");
  let groups = $state<Group[]>([]);
  let partial = $state<string[]>([]);
  let searching = $state(false);
  let note = $state<string | null>(null);
  let open = $state(false);

  const portalName = (id: string) => estate.portals.find((p) => p.id === id)?.name ?? id;
  const total = $derived(groups.reduce((n, g) => n + g.hits.length, 0));

  async function run(e: Event) {
    e.preventDefault();
    if (!q.trim()) return;
    searching = true;
    open = true;
    note = null;
    partial = [];
    try {
      const res = await api.get<{ groups: Group[]; total: number; partial: string[]; searchable: string[] }>(
        `/search?q=${encodeURIComponent(q.trim())}`,
      );
      groups = res.groups;
      partial = res.partial;
      if (res.total === 0) {
        // Three genuinely different answers, and conflating them is how a search box
        // loses trust: nothing is searchable yet, nothing answered, nothing matched.
        note =
          res.searchable.length === 0
            ? "No portal offers search yet — Vault is the first that will."
            : res.partial.length === res.searchable.length
              ? "Nothing answered in time. Try again."
              : "Nothing found.";
      }
    } catch (err) {
      note = err instanceof ApiError ? err.message : "Search is unavailable.";
      groups = [];
    } finally {
      searching = false;
    }
  }

  function clear() {
    q = "";
    groups = [];
    partial = [];
    note = null;
    open = false;
  }
</script>

<div class="box">
  <form class="field" onsubmit={run} role="search">
    <Icon name="search" size={15} />
    <input
      bind:value={q}
      placeholder="Search every portal…"
      aria-label="Search every portal"
      onfocus={() => (open = groups.length > 0 || note !== null)}
    />
    {#if searching}<span class="dots" aria-label="Searching">…</span>{/if}
    {#if !searching && (groups.length > 0 || note)}
      <button type="button" class="x" onclick={clear} aria-label="Clear search"><Icon name="x" size={14} /></button>
    {/if}
  </form>

  {#if open && (groups.length > 0 || note || partial.length > 0)}
    <div class="pop" role="dialog" aria-label="Search results">
      {#if note}<p class="pop__note">{note}</p>{/if}

      {#each groups as g (g.portalId)}
        <div class="pop__group">{g.name}</div>
        {#each g.hits as h (h.url + h.title)}
          <a class="hit" href={h.url}>
            <span class="hit__t">{h.title}</span>
            {#if h.subtitle}<span class="hit__s">{h.subtitle}</span>{/if}
          </a>
        {/each}
      {/each}

      {#if partial.length > 0}
        <!-- Named, never silently dropped: an omitted portal reads as "not there". -->
        <p class="pop__note pop__note--warn">
          <Icon name="alert" size={12} />
          Not included: {partial.map(portalName).join(", ")} did not answer in time.
        </p>
      {/if}

      {#if total > 0}
        <button class="pop__close" onclick={() => (open = false)}>Close</button>
      {/if}
    </div>
  {/if}
</div>

<style>
  .box {
    position: relative;
    flex: 1;
    max-width: 420px;
    min-width: 0;
  }
  .field {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 11px;
    border-radius: var(--r-pill);
    background: var(--fill);
    box-shadow: inset 0 0 0 1px var(--line);
    color: var(--mut);
  }
  .field:focus-within {
    box-shadow: inset 0 0 0 1px var(--acc);
  }
  .field input {
    flex: 1;
    min-width: 0;
    background: none;
    border: none;
    outline: none;
    padding: 0;
    font-size: 0.7812rem;
    color: var(--tx);
  }
  .dots {
    font-size: 0.75rem;
  }
  .x {
    padding: 2px;
    color: var(--mut);
    border-radius: var(--r-control);
  }
  .x:hover {
    color: var(--acc);
  }

  .pop {
    position: absolute;
    top: calc(100% + 7px);
    right: 0;
    left: 0;
    z-index: 40;
    max-height: 62vh;
    overflow-y: auto;
    padding: 6px;
    border-radius: var(--r-card, 14px);
    background: var(--s1);
    box-shadow:
      inset 0 0 0 1px var(--line),
      0 18px 40px rgb(0 0 0 / 0.35);
  }
  .pop__group {
    padding: 8px 10px 4px;
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: var(--mut);
  }
  .pop__note {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    padding: 10px;
    font-size: 0.75rem;
    color: var(--mut);
  }
  .pop__note--warn {
    color: var(--warn);
    font-weight: 700;
  }
  .hit {
    display: block;
    padding: 8px 10px;
    border-radius: var(--r-control);
    color: var(--tx);
  }
  .hit:hover {
    background: var(--card-hover);
  }
  .hit__t {
    display: block;
    font-size: 0.7812rem;
    font-weight: 700;
  }
  .hit__s {
    display: block;
    margin-top: 1px;
    font-size: 0.72rem;
    color: var(--mut);
  }
  .pop__close {
    width: 100%;
    padding: 8px;
    margin-top: 2px;
    border-top: 1px solid var(--line);
    font-size: 0.72rem;
    font-weight: 700;
    color: var(--mut);
  }

  @media (max-width: 620px) {
    /* The popover would be wider than the viewport if it stayed anchored to a
       shrunken field, so it spans the screen instead. */
    .pop {
      position: fixed;
      top: 62px;
      right: 8px;
      left: 8px;
    }
  }
</style>
